/**
 * Ported VERBATIM from `apr_template/src/pages/FinalApplicationDispositionModel.tsx`.
 * Only import paths change. See ../compat/ for the shims.
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../compat/AuthContext';
import { authFetch } from '../compat/api';
import { getAppAnalyses, type AppAnalyses, type AnalysisHistoryItem } from '../compat/aiRepositoryApi';
import './FinalApplicationDispositionModel.css';
import { dispositionColor, useDispositionPalette } from '../dispositionPalette';
import './IntegrationApplicationMasterData.css';
import './modelStatsBadge.css';
import './FinalDispositionPanel.css';
import { ThemeProvider } from '@mui/material/styles';
import { Icon, Sheet, WorkspaceThemeProvider, icons, WS_COLOR, WS_FONT, WS_RADIUS, WS_STATUS, WS_TYPE, WS_CONTROL } from '@platform/ui-kit/workspace';
import './FinalDispositionWorkspace.css';

/**
 * The kit tokens as CSS variables on this screen's root (owner direction 2026-10-21: this
 * screen follows the Decision Workspace design). FinalDispositionWorkspace.css reads only
 * these, so every value is the kit's.
 */
const FDM_WS_VARS = ((): React.CSSProperties => {
  const v: Record<string, string> = {
    '--ws-font': WS_FONT.sans, '--ws-mono': WS_FONT.mono,
    '--ws-bg': WS_COLOR.background, '--ws-surface': WS_COLOR.surface, '--ws-surface-subtle': WS_COLOR.surfaceSubtle,
    '--ws-text': WS_COLOR.text, '--ws-text-muted': WS_COLOR.textMuted, '--ws-border': WS_COLOR.border,
    '--ws-field-outline': WS_COLOR.fieldOutline, '--ws-primary': WS_COLOR.primary, '--ws-on-primary': WS_COLOR.onPrimary,
    '--ws-focus': WS_COLOR.focus, '--ws-tonal': WS_COLOR.tonal, '--ws-on-tonal': WS_COLOR.onTonal, '--ws-tonal-hover': WS_COLOR.tonalHover,
    '--ws-r-chip': `${WS_RADIUS.chip}px`, '--ws-r-field': `${WS_RADIUS.field}px`, '--ws-r-card': `${WS_RADIUS.card}px`, '--ws-r-button': `${WS_RADIUS.button}px`,
    '--ws-control-h': `${WS_CONTROL.height}px`, '--ws-row-h': `${WS_CONTROL.rowHeight}px`,
  };
  for (const [tone, pair] of Object.entries(WS_STATUS)) { v[`--ws-${tone}`] = pair.fill; v[`--ws-on-${tone}`] = pair.ink; }
  for (const [role, [size, line, weight]] of Object.entries(WS_TYPE)) {
    const key = role.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
    v[`--ws-fs-${key}`] = `${size}px`; v[`--ws-lh-${key}`] = `${line}px`; v[`--ws-fw-${key}`] = String(weight);
  }
  return v as React.CSSProperties;
})();

/**
 * The kit Sheet renders IN PLACE (inside `.apr-app`, so the panel stylesheet reaches its
 * body) and above the app bar and App Rationalization's own overlays (1500).
 */
type ThemeLike = { zIndex?: Record<string, number>; components?: Record<string, { defaultProps?: object }> };
const panelInPlace = (outer: object) => {
  const t = outer as ThemeLike;
  const components = { ...(t.components ?? {}) };
  for (const name of ['MuiModal', 'MuiDrawer']) {
    components[name] = { ...(components[name] ?? {}), defaultProps: { ...(components[name]?.defaultProps ?? {}), disablePortal: true } };
  }
  return { ...outer, zIndex: { ...(t.zIndex ?? {}), drawer: 1600, modal: 1600 }, components };
};
import HelpPanel from '../compat/HelpPanel';
import { type Help } from '../compat/helpTypes';

/**
 * The full final-disposition vocabulary — the four 4R families and their sub-options.
 * The final decision is no longer capped at the four parents; a Custom label is also
 * allowed. The saved value is the rich label; its 4R parent is derived server-side.
 */
const FINAL_DISPOSITION_OPTIONS: { family: string; color: string; options: string[] }[] = [
  // `color` is resolved from the matrix at render through `richDispColor`; the literals
  // below are only a fallback for the first paint before the matrix has loaded.
  // Owner ruling 2026-10-21: every final disposition names its family first.
  { family: 'Retain', color: '#208637', options: ['Retain', 'Retain – Renegotiate', 'Retain – Right-Size'] },
  { family: 'Re-Platform', color: '#b1580e', options: ['Re-Platform – Consolidate', 'Re-Platform – Cloud Migration', 'Re-Platform – Replace'] },
  { family: 'Re-Design', color: '#8c6a04', options: ['Re-Design – Modernize'] },
  { family: 'Retire', color: '#dc3545', options: ['Retire'] },
];

/** Coarse 4R parent of a rich disposition label (mirrors the server's derive4RParent). */
function parent4R(label?: string | null): string | null {
  if (!label) return null;
  const k = String(label).toUpperCase();
  if (k.includes('RETIRE')) return 'Retire';
  if (k.includes('RE-DESIGN') || k.includes('MODERNIZE')) return 'Re-Design';
  if (k.includes('CONSOLIDAT') || k.includes('CLOUD') || k.includes('REPLACE') || k.startsWith('RE-PLATFORM')) return 'Re-Platform';
  if (k.includes('RETAIN') || k.includes('RENEGOT') || k.includes('RIGHT')) return 'Retain';
  return null;
}

/**
 * "Family – Sub" form of a final disposition (mirrors the server's normalizeFinalLabel,
 * which is the authority — this copy only keeps the panel's preview honest before save).
 * A Strategic Intelligence "CLOUD MIGRATION" becomes "Re-Platform – Cloud Migration".
 */
function normalizeFinalLabel(label?: string | null): string {
  const raw = String(label ?? '').trim();
  if (!raw) return raw;
  const k = raw.toUpperCase().replace(/\s+/g, ' ');
  const parent = parent4R(raw);
  if (!parent) return raw;
  const sub = (s: string) => `${parent} – ${s}`;
  switch (parent) {
    case 'Retain':
      if (k.includes('RENEGOT')) return sub('Renegotiate');
      if (k.includes('RIGHT')) return sub('Right-Size');
      if (k === 'RETAIN') return 'Retain';
      break;
    case 'Re-Platform':
      if (k.includes('CONSOLIDAT')) return sub('Consolidate');
      if (k.includes('CLOUD')) return sub('Cloud Migration');
      if (k.includes('REPLACE')) return sub('Replace');
      if (k === 'RE-PLATFORM') return 'Re-Platform';
      break;
    case 'Re-Design':
      if (k.includes('MODERNIZE')) return sub('Modernize');
      if (k === 'RE-DESIGN') return 'Re-Design';
      break;
    case 'Retire':
      if (k === 'RETIRE') return 'Retire';
      break;
  }
  return k.startsWith(parent.toUpperCase()) ? raw : sub(raw);
}

/**
 * Colour for a rich label, via its 4R parent — FROM THE DISPOSITION MATRIX.
 *
 * This was a hardcoded set, and the FIFTH palette in the product for the same four words: it
 * drew Re-Design in PURPLE while the matrix says amber and the scoring screen said blue. The
 * matrix is now the only source, so an administrator editing a colour there moves every screen
 * at once — which is what a colour picker on a settings screen is supposed to mean.
 *
 * A custom label whose parent cannot be derived keeps a neutral slate: no disposition, no hue.
 *
 * `ink`, not the stored value, because these are drawn as text and 1px rules. The stored
 * colours are fills — Re-Design's #ffc107 reads at 1.63:1 on white.
 */
function richDispColor(label?: string | null): string {
  const parent = parent4R(label);
  return parent ? dispositionColor(parent).ink : '#546e7a';
}

/**
 * A disposition as a FILLED chip in the matrix's own colour (§1.9.3 rule 2) — used by the
 * editor panel. A label with no derivable family gets no style, so the panel's neutral chip
 * (the DESIGN.md neutral pair) shows instead.
 */
function dispChipStyle(label?: string | null): React.CSSProperties | undefined {
  const parent = parent4R(label);
  if (!parent) return undefined;
  const c = dispositionColor(parent);
  return { background: c.base, color: c.onBase };
}

const FinalApplicationDispositionModelHelp: Help = {
  title: 'Final Disposition',
  icon: '🎯',
  purpose:
    'Confirm or <strong>override</strong> each application\'s initial recommendation to set the <strong>Final Disposition</strong> — the locked decision that carries into integration planning. The original scoring is preserved unchanged.',
  doHere: [
    'Click a <strong>Final Disposition</strong> badge to open the editor, pick the disposition, and enter a required <strong>reason</strong> for any change.',
    'Add sub-options where relevant: <strong>License Renegotiation</strong> (Retain) or <strong>Consolidate / Cloud Migration / Replace</strong> (Re-Platform).',
    'Click <strong>Transfer to Planning &amp; Integration</strong> to lock an application and hand it to the planning &amp; integration process (irreversible).',
    'Search, filter by <strong>Original</strong> / <strong>Final</strong> recommendation, sort columns, and expand/collapse APR model groups.',
    'Use <strong>Download Data</strong> to export the disposition table as CSV; each APR model group header shows its own apps / completed / pending counts.',
  ],
  fits:
    'Application Evaluation &rarr; Final Disposition',
  firstTime:
    'Open an application\'s Final Disposition badge, confirm or change the disposition with a reason, then Save.',
  sections: [
    {
      type: 'defs',
      label: 'Disposition options',
      items: [
        { term: 'Retire', color: '#7f1d1d', def: '1.00&ndash;2.00 &mdash; decommission.' },
        { term: 'Re-Platform', color: '#9a3412', def: '2.01&ndash;3.00 &mdash; move platform (consolidate / cloud / replace).' },
        { term: 'Re-Design', color: '#9a3412', def: '3.01&ndash;4.00 &mdash; modernize / rebuild.' },
        { term: 'Retain', color: '#14532d', def: '4.01&ndash;5.00 &mdash; keep (optional license renegotiation).' },
      ],
      note: 'Overriding a disposition requires a reason; transferred applications are locked and cannot be edited.',
    },
  ],
};

interface DispositionApplication {
  id: string;
  apr_model_id: string;
  apr_model_name: string;
  application_id: string;
  application_id_code: string;
  application_name: string;
  original_4r_recommendation: 'Retire' | 'Re-Platform' | 'Re-Design' | 'Retain';
  overall_score: number;
  final_4r_model: 'Retire' | 'Re-Platform' | 'Re-Design' | 'Retain' | null;
  // The rich final label (any of the sub-vocabulary or a custom label) + its provenance.
  final_disposition?: string | null;
  final_disposition_source?: 'manual' | 'strategic_intelligence' | null;
  final_source_run_app_id?: string | null;
  created_at: string;
  updated_at: string;
  updated_by?: string;
  changed_by_user_id?: string;
  change_reason?: string;
  transferred_to_integration?: boolean;
  transferred_at?: string;
  transferred_by?: string;
  retain_confirmed?: boolean;
  retain_confirmed_at?: string;
  retain_confirmed_by?: string;
  license_renegotiation?: boolean;
  consolidate?: boolean;
  consolidate_target_app?: string;
  cloud_migration?: boolean;
  replace_app?: boolean;
  replace_target_app?: string;
}

interface ReadyForTransferApplication {
  application_id: string;
  application_id_code: string;
  application_name: string;
  recommendation: 'Retire' | 'Re-Platform' | 'Re-Design' | 'Retain';
  overall_score: number;
  completed_at_formatted: string;
}

interface APRModelGroup {
  modelId: string;
  modelName: string;
  modelStatus: string;
  applications: DispositionApplication[];
}

const FinalApplicationDispositionModel: React.FC = () => {
  /*
   * Subscribe so the screen REPAINTS when the matrix arrives.
   *
   * `richDispColor` is a plain function and reads the palette synchronously, which is right —
   * it is called from a dozen places that are not components. But a plain read cannot tell
   * React that the colours changed, so without this line the first paint's fallback values
   * would stay on screen until something else happened to re-render.
   */
  useDispositionPalette();

  const { currentUser } = useAuth();
  const [applications, setApplications] = useState<DispositionApplication[]>([]);
  const [collapsedModels, setCollapsedModels] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingCell, setEditingCell] = useState<{appId: string, field: string} | null>(null);
  const [editingModal, setEditingModal] = useState<{
    appId: string;               // final_disposition_model row id
    applicationId: string;       // the applications.id (for fetching its analyses)
    appName: string;
    currentValue: string | null; // original Q&A recommendation (4R)
    newValue: string;            // the rich final disposition label
    reason: string;              // the Justification narrative
    // How the decision is being made, and (for SI) which analysis it came from.
    dispositionSource: 'manual' | 'strategic_intelligence';
    sourceRunAppId: string | null;
    mode: 'manual' | 'adopt';    // which editor pane is shown
    consolidateTargetApp: string;
    replaceTargetApp: string;
    // The analysis pinned as final for this app (null = none) — enables Adopt mode.
    finalPin: AnalysisHistoryItem | null;
    qaDisposition: string | null;
    qaScore: number | string | null;
  } | null>(null);
  const [transferModal, setTransferModal] = useState<{
    show: boolean;
    applications: ReadyForTransferApplication[];
    selectedApps: Set<string>;
    loading: boolean;
  }>({
    show: false,
    applications: [],
    selectedApps: new Set(),
    loading: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterOriginal, setFilterOriginal] = useState<string>('All');
  const [filterFinal, setFilterFinal] = useState<string>('All');
  const [sortField, setSortField] = useState<string>('application_name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10000);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    totalApplications: 0,
    completedDispositions: 0,
    pendingDispositions: 0,
    distribution: {
      'Retire': 0,
      'Retain': 0,
      'Re-Platform': 0,
      'Re-Design': 0
    }
  });

  const [initialized, setInitialized] = useState(false);

  // Derive modelGroups from applications using useMemo so it updates when applications change
  const modelGroups = React.useMemo(() => {
    // Group applications by APR Model
    const groupedByModel = applications.reduce((acc: Record<string, APRModelGroup>, app: DispositionApplication) => {
      const modelId = app.apr_model_id || 'unknown';
      const modelName = app.apr_model_name || 'Unknown Model';

      if (!acc[modelId]) {
        acc[modelId] = {
          modelId,
          modelName,
          modelStatus: 'Active',
          applications: []
        };
      }
      acc[modelId].applications.push(app);
      return acc;
    }, {});

    // Convert to array and sort by model name
    return (Object.values(groupedByModel) as APRModelGroup[]).sort((a, b) =>
      a.modelName.localeCompare(b.modelName)
    );
  }, [applications]);

  // Initialize disposition data on first load to sync with scored applications
  useEffect(() => {
    const initializeDispositionData = async () => {
      try {
        // Sync any newly scored applications to the disposition table
        await authFetch('/api/final-disposition/initialize', {
          method: 'POST',
          body: JSON.stringify({ createdBy: 'system' })
        });
      } catch (error) {
        console.error('Failed to initialize disposition data:', error);
      } finally {
        setInitialized(true);
      }
    };
    initializeDispositionData();
  }, []); // Run once on component mount

  // Debounce search term so API is not called on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (initialized) {
      loadDispositionData();
    }
  }, [initialized, currentPage, pageSize, debouncedSearchTerm, filterFinal, sortField, sortDirection]);

  const loadDispositionData = async () => {
    if (!initialLoadDone) setLoading(true);
    setError(null);

    try {
      const statusFilter = filterFinal === 'All' ? 'all' :
                          filterFinal === 'Pending' ? 'pending' : filterFinal;

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
        search: debouncedSearchTerm,
        statusFilter,
        sortBy: sortField,
        sortOrder: sortDirection
      });

      const response = await authFetch(`/api/final-disposition?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch disposition data');
      }

      const result = await response.json();

      if (result.success) {
        setApplications(result.data);
        setTotalCount(result.pagination.total);
        // Map new backend field names to what this page expects
        setStats({
          totalApplications: result.stats.totalApplications || 0,
          completedDispositions: result.stats.dispositionSet || result.stats.completedDispositions || 0,
          pendingDispositions: result.stats.pendingDispositions || 0,
          distribution: result.stats.distribution || { 'Retire': 0, 'Retain': 0, 'Re-Platform': 0, 'Re-Design': 0 }
        });

        /*
         * COLLAPSE ONLY WHEN THERE IS SOMETHING TO COLLAPSE.
         *
         * Every group was collapsed on first load. With one model holding one application
         * that means the screen opens EMPTY — a header, a group title, and nothing else —
         * and it reads as "this screen is broken" or "my changes did not apply", because
         * there is literally nothing on it to look at.
         *
         * A collapsed overview earns its keep across a real estate of many models. Below
         * that it just hides the only thing on the page. Five is the line: enough that a
         * list of titles is a useful summary, few enough that opening them all is not a
         * wall of rows.
         */
        if (collapsedModels.size === 0 && result.data.length > 0) {
          const allModelIds = result.data.reduce((ids: Set<string>, app: DispositionApplication) => {
            ids.add(app.apr_model_id || 'unknown');
            return ids;
          }, new Set<string>());
          if (allModelIds.size > 5) setCollapsedModels(allModelIds);
        }
      } else {
        throw new Error(result.message || 'Unknown error occurred');
      }
    } catch (err: any) {
      console.error('Error loading disposition data:', err);
      setError(err.message || 'Failed to load disposition data');
      // Fallback to empty state
      setApplications([]);
      setTotalCount(0);
      setStats({
        totalApplications: 0,
        completedDispositions: 0,
        pendingDispositions: 0,
        distribution: { 'Retire': 0, 'Retain': 0, 'Re-Platform': 0, 'Re-Design': 0 }
      });
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  };

  // Toggle collapse/expand for a single model
  const toggleModelCollapse = (modelId: string) => {
    setCollapsedModels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(modelId)) {
        newSet.delete(modelId);
      } else {
        newSet.add(modelId);
      }
      return newSet;
    });
  };

  // Expand all models
  const handleExpandAll = () => {
    setCollapsedModels(new Set());
  };

  // Collapse all models
  const handleCollapseAll = () => {
    const allModelIds = modelGroups.map(group => group.modelId);
    setCollapsedModels(new Set(allModelIds));
  };


  // SCREEN LOCK (CLAUDE.md "A screen header never scrolls"): the page never scrolls; the
  // title, filters and model headers stay put and only the model groups scroll, with their
  // column headings anchored. The height is MEASURED (innerHeight − top − <main>'s bottom
  // padding) because the shell's <main> grows; a callback ref because the root mounts after
  // the loading gate.
  const [lockEl, setLockEl] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!lockEl) return;
    const main = lockEl.closest('main');
    const measure = () => {
      const top = lockEl.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      lockEl.style.height = `${Math.max(320, Math.floor(window.innerHeight - top - inset))}px`;
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [lockEl]);

  // The editor panel's own message line (DESIGN.md: messages are banners, not browser alerts).
  const [panelError, setPanelError] = useState<string | null>(null);

  // Focus moves in, is trapped and returns to the opener by the kit Sheet (MUI Drawer).
  const closeEditingPanel = () => {
    setEditingModal(null);
    setPanelError(null);
  };

  const openEditingModal = (appId: string) => {
    const app = applications.find(a => a.id === appId);
    if (!app) return;
    setPanelError(null);
    const existingLabel = normalizeFinalLabel((app as any).final_disposition || app.final_4r_model || '');
    const existingSource: 'manual' | 'strategic_intelligence' =
      (app as any).final_disposition_source === 'strategic_intelligence' ? 'strategic_intelligence' : 'manual';
    // Open immediately with what we know; the pinned-final analysis (which enables the
    // Adopt pane) is fetched in the background and folded in when it arrives.
    setEditingModal({
      appId,
      applicationId: app.application_id,
      appName: app.application_name,
      currentValue: app.original_4r_recommendation,
      newValue: existingLabel,
      reason: app.change_reason || '',
      dispositionSource: existingSource,
      sourceRunAppId: (app as any).final_source_run_app_id || null,
      mode: 'manual',
      consolidateTargetApp: app.consolidate_target_app || '',
      replaceTargetApp: app.replace_target_app || '',
      finalPin: null,
      qaDisposition: app.original_4r_recommendation || null,
      qaScore: app.overall_score ?? null,
    });
    // Fetch the app's analyses to find the one pinned as final (Adopt mode).
    getAppAnalyses(app.application_id).then((res: AppAnalyses) => {
      const pin = res.finalRunAppId ? res.analyses.find(a => a.run_app_id === res.finalRunAppId) || null : null;
      setEditingModal(prev => {
        if (!prev || prev.appId !== appId) return prev; // modal changed/closed meanwhile
        // Default to Adopt when a final analysis is pinned and nothing decided yet, and
        // PRE-FILL the disposition + Justification from that analysis so the narrative the
        // user is looking at is already in the field (no extra "adopt" click needed).
        const startAdopt = !!pin && !existingLabel;
        return {
          ...prev,
          finalPin: pin,
          qaDisposition: res.qa?.completed ? (res.qa?.disposition || prev.qaDisposition) : prev.qaDisposition,
          qaScore: res.qa?.overall_score ?? prev.qaScore,
          mode: startAdopt ? 'adopt' : prev.mode,
          ...(startAdopt && pin ? {
            newValue: normalizeFinalLabel(pin.disposition) || prev.newValue,
            reason: pin.disposition_justification || pin.rationale || prev.reason,
            dispositionSource: 'strategic_intelligence' as const,
            sourceRunAppId: pin.run_app_id,
          } : {}),
        };
      });
    }).catch(() => { /* no analyses / not permitted — Adopt pane just won't appear */ });
  };

  // Fill the editor from the pinned-final analysis: its disposition + its narrative as
  // the Justification. This is the "choose the narrative from the Analysis Repository" path.
  const adoptFromAnalysis = () => {
    setEditingModal(prev => {
      if (!prev || !prev.finalPin) return prev;
      return {
        ...prev,
        newValue: normalizeFinalLabel(prev.finalPin.disposition) || prev.newValue,
        reason: prev.finalPin.disposition_justification || prev.finalPin.rationale || prev.reason,
        dispositionSource: 'strategic_intelligence',
        sourceRunAppId: prev.finalPin.run_app_id,
      };
    });
  };

  const handleModalSave = async () => {
    if (!editingModal) return;

    const { appId, newValue, reason, dispositionSource, sourceRunAppId, consolidateTargetApp, replaceTargetApp } = editingModal;
    const label = normalizeFinalLabel(newValue);
    const parent = parent4R(label);

    if (!label) {
      setPanelError('Please choose a final disposition.');
      return;
    }
    // A Justification is always required — written for a manual decision, or the adopted
    // analysis narrative for a Strategic-Intelligence one.
    if (!reason.trim()) {
      setPanelError('A justification is required. For a Strategic-Intelligence decision, adopt the analysis to fill it; otherwise write one.');
      return;
    }
    if (reason.length > 4000) {
      setPanelError('Justification cannot exceed 4000 characters.');
      return;
    }
    setPanelError(null);

    // Target app only meaningful for Consolidate / Replace.
    const isConsolidate = /CONSOLIDAT/i.test(label);
    const isReplace = /REPLACE/i.test(label);

    try {
      // Optimistic update.
      setApplications(prev => prev.map(app =>
        app.id === appId
          ? {
              ...app,
              final_4r_model: parent as any,
              final_disposition: label,
              final_disposition_source: dispositionSource,
              final_source_run_app_id: sourceRunAppId,
              updated_at: new Date().toISOString(),
              updated_by: currentUser?.name || 'Unknown User',
              changed_by_user_id: currentUser?.id || 'unknown',
              change_reason: reason,
              consolidate: isConsolidate,
              consolidate_target_app: isConsolidate ? consolidateTargetApp : '',
              cloud_migration: /CLOUD/i.test(label),
              replace_app: isReplace,
              replace_target_app: isReplace ? replaceTargetApp : '',
              license_renegotiation: /RENEGOT/i.test(label),
            }
          : app
      ));

      setEditingModal(null);

      // Persist. The server derives the 4R parent and the sub-flags from the label.
      const response = await authFetch(`/api/final-disposition/${appId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          final_disposition: label,
          justification: reason,
          final_disposition_source: dispositionSource,
          final_source_run_app_id: dispositionSource === 'strategic_intelligence' ? sourceRunAppId : null,
          updated_by: currentUser?.name || 'Unknown User',
          changed_by_user_id: currentUser?.id || 'unknown',
          consolidate_target_app: isConsolidate ? consolidateTargetApp : null,
          replace_target_app: isReplace ? replaceTargetApp : null,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update final disposition');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to update final disposition');
      }

      // Update local state with server response to ensure consistency, preserving change_reason
      setApplications(prev => prev.map(app => 
        app.id === appId ? { 
          ...app, 
          ...result.data,
          change_reason: result.data.change_reason || reason // Ensure change_reason is preserved
        } : app
      ));

      // Update stats - only reload stats, not all data to preserve the change_reason display
      const statsResponse = await authFetch('/api/final-disposition?page=1&limit=1');
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData.stats);
      }
      
    } catch (err: any) {
      console.error('Failed to update final disposition:', err);
      window.alert(`Error: ${err.message || 'Failed to update final disposition'}`);
      // Revert optimistic update by reloading data
      await loadDispositionData();
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleConfirmRetain = async (app: DispositionApplication) => {
    const confirmMessage = `Are you sure you want to confirm the Retain decision for application ${app.application_id_code} ${app.application_name}? This action cannot be undone and the application will be locked from further changes.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await authFetch(`/api/final-disposition/confirm-retain/${app.id}`, {
        method: 'POST',
        body: JSON.stringify({
          confirmed_by: currentUser?.name || 'Unknown User'
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to confirm retain decision');
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to confirm retain decision');
      }

      // Update the local state to reflect the confirmation
      setApplications(prevApps =>
        prevApps.map(prevApp =>
          prevApp.id === app.id
            ? { ...prevApp, ...result.data }
            : prevApp
        )
      );

      // Update stats - only reload stats, not all data
      const statsResponse = await authFetch('/api/final-disposition?page=1&limit=1');
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData.stats);
      }

      window.alert(`Successfully confirmed Retain decision for "${app.application_name}"!`);
    } catch (error: any) {
      console.error('Error confirming retain decision:', error);
      window.alert(`Error confirming retain decision: ${error.message}`);
    }
  };

  const handleTransferToIntegration = async (app: DispositionApplication) => {
    // Check if Final Disposition is set
    if (!app.final_4r_model) {
      window.alert(`Cannot transfer "${app.application_name}" to Integration.\n\nPlease set the Final Disposition first before transferring to Integration.`);
      return;
    }

    const confirmMessage = `Are you sure that you want to transfer application ${app.application_id_code} ${app.application_name} to Integration process. State that changes can not be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await authFetch(`/api/final-disposition/transfer-to-integration/${app.id}`, {
        method: 'POST',
        body: JSON.stringify({
          transferred_by: currentUser?.name || 'Unknown User'
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to transfer to integration');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to transfer to integration');
      }

      // Update the specific application in state
      setApplications(prevApps =>
        prevApps.map(prevApp =>
          prevApp.id === app.id
            ? { ...prevApp, ...result.data }
            : prevApp
        )
      );

      window.alert(`Successfully transferred "${app.application_name}" to Integration!`);
    } catch (error: any) {
      console.error('Error transferring to integration:', error);
      window.alert(`Error transferring to integration: ${error.message}`);
    }
  };

  const openTransferModal = async () => {
    setTransferModal(prev => ({ ...prev, show: true, loading: true }));

    try {
      const response = await authFetch('/api/final-disposition/ready-for-transfer');
      if (!response.ok) {
        throw new Error('Failed to fetch applications ready for transfer');
      }

      const result = await response.json();
      
      if (result.success) {
        setTransferModal(prev => ({
          ...prev,
          applications: result.data,
          selectedApps: new Set(),
          loading: false
        }));
      } else {
        throw new Error(result.message || 'Failed to load ready applications');
      }
    } catch (error: any) {
      console.error('Error loading applications ready for transfer:', error);
      window.alert(`Error: ${error.message}`);
      setTransferModal(prev => ({ ...prev, show: false, loading: false }));
    }
  };

  const closeTransferModal = () => {
    setTransferModal({
      show: false,
      applications: [],
      selectedApps: new Set(),
      loading: false
    });
  };

  const toggleAppSelection = (appId: string) => {
    setTransferModal(prev => {
      const newSelected = new Set(prev.selectedApps);
      if (newSelected.has(appId)) {
        newSelected.delete(appId);
      } else {
        newSelected.add(appId);
      }
      return { ...prev, selectedApps: newSelected };
    });
  };

  const selectAllApps = () => {
    setTransferModal(prev => ({
      ...prev,
      selectedApps: new Set(prev.applications.map(app => app.application_id))
    }));
  };

  const clearAllApps = () => {
    setTransferModal(prev => ({
      ...prev,
      selectedApps: new Set()
    }));
  };

  const executeTransfer = async () => {
    const selectedCount = transferModal.selectedApps.size;
    if (selectedCount === 0) {
      window.alert('Please select at least one application to transfer.');
      return;
    }

    const confirmMessage = `Are you sure you want to transfer ${selectedCount} application(s) to the Final Disposition Model? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setTransferModal(prev => ({ ...prev, loading: true }));

    try {
      const response = await authFetch('/api/final-disposition/manual-transfer', {
        method: 'POST',
        body: JSON.stringify({
          applicationIds: Array.from(transferModal.selectedApps),
          transferredBy: currentUser?.name || 'Unknown User'
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to execute manual transfer');
      }

      const result = await response.json();
      
      if (result.success) {
        window.alert(`Successfully transferred ${result.recordsAdded} applications to the Final Disposition Model!`);
        closeTransferModal();
        await loadDispositionData(); // Reload the main data to show new records
      } else {
        throw new Error(result.message || 'Failed to execute transfer');
      }
    } catch (error: any) {
      console.error('Error executing transfer:', error);
      window.alert(`Error: ${error.message}`);
    } finally {
      setTransferModal(prev => ({ ...prev, loading: false }));
    }
  };

  const downloadDispositionData = () => {
    if (applications.length === 0) {
      alert('No disposition data to download');
      return;
    }

    const headers = [
      'Application ID Code',
      'Application Name',
      'APR Model',
      'Original Recommendation',
      'Overall Score',
      'Final Disposition',
      'Change Reason',
      'License Renegotiation',
      'Consolidate',
      'Consolidate Target App',
      'Cloud Migration',
      'Replace',
      'Replace Target',
      'Changed By',
      'Updated Date',
      'Transferred to Integration',
      'Transferred Date',
      'Transferred By'
    ];

    const csvRows = [headers.join(',')];

    applications.forEach(app => {
      const row = [
        `"${(app.application_id_code || '').replace(/"/g, '""')}"`,
        `"${(app.application_name || '').replace(/"/g, '""')}"`,
        `"${(app.apr_model_name || '').replace(/"/g, '""')}"`,
        `"${(app.original_4r_recommendation || '').replace(/"/g, '""')}"`,
        app.overall_score?.toFixed(2) || '',
        `"${(app.final_4r_model || '').replace(/"/g, '""')}"`,
        `"${(app.change_reason || '').replace(/"/g, '""')}"`,
        app.license_renegotiation ? 'Yes' : '',
        app.consolidate ? 'Yes' : '',
        `"${(app.consolidate_target_app || '').replace(/"/g, '""')}"`,
        app.cloud_migration ? 'Yes' : '',
        app.replace_app ? 'Yes' : '',
        `"${(app.replace_target_app || '').replace(/"/g, '""')}"`,
        `"${(app.updated_by || '').replace(/"/g, '""')}"`,
        app.updated_at ? new Date(app.updated_at).toLocaleDateString() : '',
        app.transferred_to_integration ? 'Yes' : 'No',
        app.transferred_at ? new Date(app.transferred_at).toLocaleDateString() : '',
        `"${(app.transferred_by || '').replace(/"/g, '""')}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `final_disposition_data_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  // Sort applications to move transferred ones to bottom and gray them out
  const sortedApplications = [...applications].sort((a, b) => {
    // Check if applications are locked (transferred to integration)
    const aLocked = a.transferred_to_integration;
    const bLocked = b.transferred_to_integration;

    // First sort by locked status - unlocked first
    if (aLocked !== bLocked) {
      return aLocked ? 1 : -1;
    }

    // Then by the current sort field
    const aValue = a[sortField as keyof typeof a] || '';
    const bValue = b[sortField as keyof typeof b] || '';
    const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  /**
   * The 4R colour, from the matrix.
   *
   * These four literals WERE the matrix's values, copied — the comments even called them
   * "canonical". Copied is the problem: editing the Disposition Matrix moved the picker and
   * nothing else. Read from the source now, so there is one place to change and no chance of
   * the copy going stale.
   */
  const getRecommendationColor = (recommendation: string | null) =>
    recommendation ? dispositionColor(recommendation).ink : '#6c757d';


  const computedStats = React.useMemo(() => {
    const completionRate = stats.totalApplications > 0 
      ? Math.round((stats.completedDispositions / stats.totalApplications) * 100) 
      : 0;
    
    return {
      total: stats.totalApplications,
      pending: stats.pendingDispositions,
      completed: stats.completedDispositions,
      completionRate
    };
  }, [stats]);

  if (loading) {
    return (
      <div className="disposition-loading">
        <div className="loading-spinner"></div>
        <p>Loading disposition data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="disposition-error">
        <h3>Error Loading Data</h3>
        <p>{error}</p>
        <button onClick={loadDispositionData} className="btn-retry">Retry</button>
      </div>
    );
  }

  return (
    <div className="page fdm-ws" style={FDM_WS_VARS} ref={setLockEl}>
      <HelpPanel help={FinalApplicationDispositionModelHelp} />
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Final Disposition Model</h1>
            <p className="page-header__subtitle">
              Override initial recommendations while preserving original scoring integrity
            </p>
          </div>
          <div className="page-header__actions">
            {applications.length > 0 && (
              <button
                className="btn fdm-btn"
                onClick={downloadDispositionData}
                title="Download current disposition data as CSV"
              >
                <Icon icon={icons.Download} size={16} /> Download Data
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="master-data-container">
        {/* Filters and Search — one row, all controls the same height. */}
        <div className="md-card fdm-filters">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'nowrap' }}>
            {/* Search Filter — allowed to shrink so the row stays on one line. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: '1 1 220px', minWidth: 0 }}>
              <label className="fdm-label">Search:</label>
              <input
                type="text"
                placeholder="Search applications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="fdm-field"
                style={{ flex: 1, minWidth: 0 }}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="fdm-btn"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Original Recommendation Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <label className="fdm-label">Original:</label>
              <select
                value={filterOriginal}
                onChange={(e) => setFilterOriginal(e.target.value)}
                className="fdm-field"
                style={{ minWidth: '130px' }}
              >
                <option value="All">All</option>
                <option value="Retire">Retire</option>
                <option value="Re-Platform">Re-Platform</option>
                <option value="Re-Design">Re-Design</option>
                <option value="Retain">Retain</option>
              </select>
            </div>

            {/* Final Disposition Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <label className="fdm-label">Final:</label>
              <select
                value={filterFinal}
                onChange={(e) => setFilterFinal(e.target.value)}
                className="fdm-field"
                style={{ minWidth: '130px' }}
              >
                <option value="All">All</option>
                <option value="Pending">Pending</option>
                <option value="Retire">Retire</option>
                <option value="Re-Platform">Re-Platform</option>
                <option value="Re-Design">Re-Design</option>
                <option value="Retain">Retain</option>
              </select>
            </div>

            {/* Page Size */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <label className="fdm-label">Show:</label>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="fdm-field"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>

            {/* Expand/Collapse Controls */}
            <div className="expand-collapse-controls" style={{ flexShrink: 0, display: 'flex', gap: '8px' }}>
              <button className="fdm-btn" onClick={handleExpandAll} title="Expand All">
                <Icon icon={icons.ChevronDown} size={16} /> Expand All
              </button>
              <button className="fdm-btn" onClick={handleCollapseAll} title="Collapse All">
                <Icon icon={icons.ChevronRight} size={16} /> Collapse All
              </button>
            </div>
          </div>
        </div>

        {/* Results Info */}
        <div className="results-info">
          <span>Showing {applications.length} of {totalCount} applications</span>
          {(searchTerm || filterFinal !== 'All') && totalCount !== computedStats.total && (
            <span className="filtered-note">(filtered from {computedStats.total} total)</span>
          )}
        </div>

        {/* Data Table - Grouped by APR Model */}
        <div className="model-groups-container">
          {modelGroups.length === 0 ? (
            /*
             * The same empty state the Q&A screen draws (`md-empty-state`, defined under
             * `.apr-app` in source-css/app.css:2196), and for the same reason: "No
             * applications match the current filter criteria" was shown even when there
             * was no data at all and no filter set, which reads as a broken screen rather
             * than as work not yet done.
             *
             * The prescriptive text follows what actually puts a row here — the INSERT in
             * server/apr/evaluation/finalDisposition.ts:169-189 takes applications whose
             * `application_scores` row is 'Completed' with a recommendation and an overall
             * score, joined to an APR model. So the route in is: model, Q&A, every question
             * answered, then the application arrives here.
             */
            <div className="md-empty-state">
              <div className="md-empty-state__icon"><Icon icon={icons.Target} size={24} /></div>
              <h3 className="md-empty-state__title">
                {computedStats.total === 0
                  ? 'No Applications Ready for Final Disposition'
                  : 'No Applications Match Your Filters'}
              </h3>
              <p className="md-empty-state__description">
                {computedStats.total === 0
                  ? 'An application arrives here once its Q&A assessment is complete and it has an initial 4R recommendation. Create an APR model, add applications to it, then use Application Portfolio Q&A to answer every question — the application appears here as soon as its scoring finishes.'
                  : searchTerm && filterFinal !== 'All'
                    ? `None of the ${computedStats.total} applications match "${searchTerm}" with the ${filterFinal} filter. Clear one of them to widen the results.`
                    : searchTerm
                      ? `None of the ${computedStats.total} applications match "${searchTerm}". Try a different application name or ID.`
                      : `None of the ${computedStats.total} applications are set to ${filterFinal}. Choose "All" to see every application.`}
              </p>
            </div>
          ) : (
            modelGroups.map(group => {
              const isCollapsed = collapsedModels.has(group.modelId);
              const completedCount = group.applications.filter(app => app.final_4r_model).length;
              const pendingCount = group.applications.length - completedCount;

              return (
                <div key={group.modelId} className="model-group">
                  {/* Model Header Row */}
                  <div
                    className="model-header-row"
                    onClick={() => toggleModelCollapse(group.modelId)}
                  >
                    <button
                      className="collapse-toggle-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleModelCollapse(group.modelId);
                      }}
                      title={isCollapsed ? 'Expand' : 'Collapse'}
                    >
                      <Icon icon={isCollapsed ? icons.ChevronRight : icons.ChevronDown} size={20} />
                    </button>
                    <span className="model-header-icon"><Icon icon={icons.Layers} size={20} /></span>
                    <div className="model-header-info">
                      <span className="model-header-name">{group.modelName}</span>
                    </div>
                    <div className="model-header-stats">
                      <span className="model-stats-badge apps">
                        <span className="model-stats-badge__n">{group.applications.length}</span> apps
                      </span>
                      <span className="model-stats-badge completed">
                        <span className="model-stats-badge__n">{completedCount}</span> completed
                      </span>
                      {pendingCount > 0 && (
                        <span className="model-stats-badge pending">
                          <span className="model-stats-badge__n">{pendingCount}</span> pending
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Applications Table - Collapsible */}
                  {!isCollapsed && (
                    <div className="table-container">
                      <table className="disposition-table">
                        <thead>
                          <tr>
                            <th onClick={() => handleSort('application_name')} className="sortable" style={{ width: '22%' }}>
                              Application {sortField === 'application_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                            </th>
                            <th onClick={() => handleSort('original_4r_recommendation')} className="sortable center col-original-4r" style={{ width: '13%' }}>
                              Original Recommendation {sortField === 'original_4r_recommendation' && (sortDirection === 'asc' ? '↑' : '↓')}
                            </th>
                            <th onClick={() => handleSort('overall_score')} className="sortable center" style={{ width: '8%' }}>
                              Score {sortField === 'overall_score' && (sortDirection === 'asc' ? '↑' : '↓')}
                            </th>
                            <th className="center editable-header" style={{ width: '23%' }}>
                              Final Disposition
                            </th>
                            <th className="center" style={{ width: '11%' }}>
                              Changed By
                            </th>
                            <th className="center" style={{ width: '9%' }}>
                              Updated
                            </th>
                            <th className="center" style={{ width: '14%' }}>
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.applications.map((app) => (
                            <tr
                              key={app.id}
                              className={app.transferred_to_integration ? 'transferred-row' : ''}
                              style={{
                                backgroundColor: app.transferred_to_integration ? WS_COLOR.surfaceSubtle : 'transparent'
                              }}
                            >
                              <td className="app-info-cell">
                                <div className="md-app-info">
                                  <div className="md-app-info__primary">{app.application_id_code}</div>
                                  <div className="md-app-info__primary">{app.application_name}</div>
                                </div>
                              </td>
                              <td className="center col-original-4r">
                                {/* Tint + ink + dot, the same chip the other two screens draw.
                                    It was a SOLID block of the disposition colour behind white
                                    text — the heaviest thing in the row, competing with the one
                                    control the row actually offers. */}
                                {/* FILLED with the matrix's own colour, like the other two
                                    screens. It was a derived tint, so the same disposition drew
                                    a different colour here than the picker on the Disposition
                                    Matrix screen showed. */}
                                <div
                                  className="recommendation-badge original compact md-chip--disposition"
                                  style={{
                                    backgroundColor: dispositionColor(app.original_4r_recommendation).base,
                                    color: dispositionColor(app.original_4r_recommendation).onBase,
                                  }}
                                >
                                  <span className="md-chip__dot" />
                                  {app.original_4r_recommendation}
                                </div>
                              </td>
                              <td className="center">
                                <div className="score-display">
                                  <span className="score-value">{app.overall_score.toFixed(2)}</span>
                                  <div className="score-bar">
                                    <div
                                      className="score-fill"
                                      style={{
                                        width: `${(app.overall_score / 5) * 100}%`,
                                        backgroundColor: getRecommendationColor(app.original_4r_recommendation)
                                      }}
                                    />
                                  </div>
                                </div>
                              </td>
                              <td className="center">
                                {/* ONE chip: the FULL final disposition on one line ("Re-Platform –
                                    Cloud Migration"), filled in its family's matrix colour. Owner
                                    direction 2026-10-21: no icon, and no separate sub-option chips
                                    or comment icon under it — the justification and any
                                    consolidate/replace target are in the chip's tooltip. */}
                                <div
                                  className={`recommendation-badge final ${app.final_4r_model ? 'set md-chip--disposition' : 'pending'}${app.transferred_to_integration ? ' is-locked' : ''}`}
                                  style={
                                    app.final_4r_model
                                      ? {
                                          backgroundColor: dispositionColor(app.final_4r_model).base,
                                          color: dispositionColor(app.final_4r_model).onBase,
                                        }
                                      : undefined
                                  }
                                  onClick={() => {
                                    if (app.transferred_to_integration) return;
                                    openEditingModal(app.id);
                                  }}
                                  title={[
                                    app.transferred_to_integration
                                      ? 'Transferred to integration — locked'
                                      : 'Click to edit final disposition (reason required)',
                                    app.consolidate && app.consolidate_target_app ? `Consolidate into: ${app.consolidate_target_app}` : '',
                                    app.replace_app && app.replace_target_app ? `Replace with: ${app.replace_target_app}` : '',
                                    app.change_reason ? `Reason: ${app.change_reason}` : '',
                                  ].filter(Boolean).join('\n')}
                                >
                                  {app.final_4r_model
                                    ? normalizeFinalLabel((app as any).final_disposition) || app.final_4r_model
                                    : 'Set disposition'}
                                </div>
                              </td>
                              <td className="center">
                                <div className="changed-by-info">
                                  {app.updated_by && app.updated_by !== 'system' ? (
                                    <div className="changed-by-user">{app.updated_by}</div>
                                  ) : (
                                    <span className="no-changes">-</span>
                                  )}
                                </div>
                              </td>
                              <td className="center">
                                <div className="update-info">
                                  <div className="update-date">
                                    {new Date(app.updated_at).toLocaleDateString()}
                                  </div>
                                </div>
                              </td>
                              <td className="center">
                                <div className="action-buttons md-action-row">
                                  {!app.transferred_to_integration ? (
                                    <button
                                      className="transfer-btn"
                                      onClick={() => handleTransferToIntegration(app)}
                                    >
                                      Transfer to Integration
                                    </button>
                                  ) : (
                                    <div className="transferred-status">
                                      <span className="fdm-chip fdm-chip--success">
                                        <Icon icon={icons.CheckCircle2} size={16} /> Transferred
                                      </span>
                                      <div className="fdm-meta">
                                        {app.transferred_at && new Date(app.transferred_at).toLocaleDateString()}
                                        {app.transferred_by && <> · by {app.transferred_by}</>}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button 
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="page-btn"
            >
              First
            </button>
            <button 
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="page-btn"
            >
              Previous
            </button>
            <span className="page-info">
              Page {currentPage} of {totalPages}
            </span>
            <button 
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="page-btn"
            >
              Next
            </button>
            <button 
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="page-btn"
            >
              Last
            </button>
          </div>
        )}
      </div>

      {/* Modals */}
      <div>
      {/* Final Disposition editor — a right-side panel (owner ruling 2026-10-21), single
          width, in the Decision Workspace design (DESIGN.md; the kit's Sheet). Header and
          action row are fixed; only the body scrolls. Disposition chips keep the
          Disposition Matrix colours (§1.9.3 rule 2). */}
      <WorkspaceThemeProvider baseline={false}>
        <ThemeProvider theme={panelInPlace}>
          <Sheet
            open={!!editingModal}
            title="Edit Final Disposition"
            onClose={closeEditingPanel}
            width={531}
            actions={editingModal ? (
              <>
                <button type="button" className="fdp-btn fdp-btn--tonal" onClick={closeEditingPanel}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="fdp-btn fdp-btn--primary"
                  onClick={handleModalSave}
                  disabled={!editingModal.newValue.trim() || !editingModal.reason.trim()}
                >
                  Save Final Disposition
                </button>
              </>
            ) : null}
          >
          {editingModal && (
            <div className="fdp-body">
              <p className="fdp-sub">Update the final disposition for this application</p>

              {panelError && (
                <div className="fdp-banner fdp-banner--error" role="alert">{panelError}</div>
              )}

              <div className="fdp-field">
                <div className="fdp-label">Application</div>
                <div className="fdp-readonly">{editingModal.appName}</div>
              </div>

              <div className="fdp-field">
                <div className="fdp-label">Original Recommendation</div>
                <div className="fdp-readonly">
                  {editingModal.currentValue ? (
                    <span className="fdp-chip" style={dispChipStyle(editingModal.currentValue)}>
                      {editingModal.currentValue}
                    </span>
                  ) : (
                    <span className="fdp-muted">Not set</span>
                  )}
                </div>
              </div>

              {/* How the final call is made: adopt an analysis pinned as final, or set it
                  manually. Adopt only works when an analysis is pinned as final for the app. */}
              <div className="fdp-field">
                <div className="fdp-label" id="fdp-mode-label">How do you want to set this?</div>
                <div className="fdp-segmented" role="group" aria-labelledby="fdp-mode-label">
                  <button
                    type="button"
                    className={`fdp-seg${editingModal.mode === 'adopt' ? ' is-on' : ''}`}
                    aria-pressed={editingModal.mode === 'adopt'}
                    disabled={!editingModal.finalPin}
                    onClick={() => setEditingModal(prev => prev ? { ...prev, mode: 'adopt' } : null)}
                  >
                    Adopt from Strategic Intelligence
                  </button>
                  <button
                    type="button"
                    className={`fdp-seg${editingModal.mode === 'manual' ? ' is-on' : ''}`}
                    aria-pressed={editingModal.mode === 'manual'}
                    onClick={() => setEditingModal(prev => prev ? { ...prev, mode: 'manual' } : null)}
                  >
                    Set manually
                  </button>
                </div>
                {!editingModal.finalPin && (
                  <div className="fdp-banner fdp-banner--info">
                    Adopt is unavailable: no Strategic Intelligence analysis is pinned as <strong>final</strong> for
                    this application. Run an analysis, then pin it as final in the Analysis Repository.
                  </div>
                )}
              </div>

              {/* ADOPT pane */}
              {editingModal.mode === 'adopt' && editingModal.finalPin && (
                <section className="fdp-card">
                  <div className="fdp-row">
                    <span className="fdp-chip" style={dispChipStyle(editingModal.finalPin.disposition)}>
                      {normalizeFinalLabel(editingModal.finalPin.disposition)}
                    </span>
                    <span className="fdp-chip fdp-chip--info">Pinned as final</span>
                    {editingModal.finalPin.confidence && (
                      <span className="fdp-meta">{editingModal.finalPin.confidence} confidence</span>
                    )}
                  </div>
                  <p className="fdp-narrative">
                    {editingModal.finalPin.disposition_justification || editingModal.finalPin.rationale || 'No narrative recorded on this analysis.'}
                  </p>
                  <div className="fdp-row">
                    <button type="button" className="fdp-btn fdp-btn--tonal" onClick={adoptFromAnalysis}>
                      Adopt “{normalizeFinalLabel(editingModal.finalPin.disposition)}” &amp; its narrative
                    </button>
                    {editingModal.newValue && editingModal.dispositionSource === 'strategic_intelligence' && (
                      <span className="fdp-chip fdp-chip--success">Adopted — review the Justification below</span>
                    )}
                  </div>
                </section>
              )}

              {/* MANUAL pane — any disposition, grouped by 4R family, plus Custom. */}
              {editingModal.mode === 'manual' && (
                <div className="fdp-field">
                  <div className="fdp-label">Final Disposition *</div>
                  {FINAL_DISPOSITION_OPTIONS.map(group => (
                    <div key={group.family} className="fdp-group">
                      <div className="fdp-group-title">{group.family}</div>
                      <div className="fdp-row">
                        {group.options.map(opt => {
                          const sel = editingModal.newValue === opt;
                          return (
                            <button
                              key={opt}
                              type="button"
                              className={`fdp-choice${sel ? ' is-on' : ''}`}
                              aria-pressed={sel}
                              style={sel ? dispChipStyle(opt) : undefined}
                              onClick={() => setEditingModal(prev => prev ? { ...prev, newValue: opt, dispositionSource: 'manual', sourceRunAppId: null } : null)}
                            >
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  <div className="fdp-group">
                    <label className="fdp-group-title" htmlFor="fdp-custom">Other</label>
                    <input
                      id="fdp-custom"
                      type="text"
                      className="fdp-input"
                      placeholder="Custom disposition — any label your model uses"
                      value={FINAL_DISPOSITION_OPTIONS.some(g => g.options.includes(editingModal.newValue)) ? '' : editingModal.newValue}
                      onChange={(e) => setEditingModal(prev => prev ? { ...prev, newValue: e.target.value, dispositionSource: 'manual', sourceRunAppId: null } : null)}
                    />
                  </div>
                  {/* Optional target application for Consolidate / Replace. */}
                  {/CONSOLIDAT/i.test(editingModal.newValue) && (
                    <input type="text" className="fdp-input fdp-gap" placeholder="Consolidate into… (target application)"
                      aria-label="Consolidate into (target application)"
                      value={editingModal.consolidateTargetApp}
                      onChange={(e) => setEditingModal(prev => prev ? { ...prev, consolidateTargetApp: e.target.value } : null)} />
                  )}
                  {/REPLACE/i.test(editingModal.newValue) && (
                    <input type="text" className="fdp-input fdp-gap" placeholder="Replace with… (replacement product)"
                      aria-label="Replace with (replacement product)"
                      value={editingModal.replaceTargetApp}
                      onChange={(e) => setEditingModal(prev => prev ? { ...prev, replaceTargetApp: e.target.value } : null)} />
                  )}
                </div>
              )}

              {/* Chosen disposition preview — shown as it will be SAVED (family first). */}
              {editingModal.newValue.trim() && (
                <div className="fdp-field fdp-row">
                  <span className="fdp-label fdp-label--inline">Final:</span>
                  <span className="fdp-chip" style={dispChipStyle(editingModal.newValue)}>
                    {normalizeFinalLabel(editingModal.newValue)}
                  </span>
                  {editingModal.dispositionSource === 'strategic_intelligence' && (
                    <span className="fdp-meta">from Strategic Intelligence</span>
                  )}
                </div>
              )}

              <div className="fdp-field">
                <label htmlFor="change-reason" className="fdp-label">Justification *</label>
                <textarea
                  id="change-reason"
                  className="fdp-input fdp-textarea"
                  value={editingModal.reason}
                  onChange={(e) => setEditingModal(prev => prev ? {...prev, reason: e.target.value} : null)}
                  placeholder={editingModal.dispositionSource === 'strategic_intelligence'
                    ? 'Adopted from the analysis narrative — edit if needed.'
                    : 'Explain the final decision — the driver, the trade-off, the risk accepted.'}
                  maxLength={4000}
                  rows={6}
                />
                <div className="fdp-meta fdp-count">{editingModal.reason.length}/4000 characters</div>
              </div>
            </div>
          )}
          </Sheet>
        </ThemeProvider>
      </WorkspaceThemeProvider>

      {/* Transfer Modal */}
      {transferModal.show && (
        <div className="modal-overlay" onClick={closeTransferModal}>
          <div className="transfer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Move Applications to Final Disposition</h2>
              <button className="modal-close" onClick={closeTransferModal}>×</button>
            </div>
            
            <div className="modal-body">
              {transferModal.loading ? (
                <div className="modal-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading applications ready for transfer...</p>
                </div>
              ) : transferModal.applications.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📋</div>
                  <h3>No Applications Ready</h3>
                  <p>All completed applications have already been transferred to the Final Disposition Model.</p>
                </div>
              ) : (
                <>
                  <div className="transfer-summary">
                    <p>Found <strong>{transferModal.applications.length}</strong> applications with completed scoring ready for transfer.</p>
                    <div className="selection-controls">
                      <button 
                        onClick={selectAllApps}
                        className="btn-select-action"
                        disabled={transferModal.loading}
                      >
                        Select All
                      </button>
                      <button 
                        onClick={clearAllApps}
                        className="btn-select-action"
                        disabled={transferModal.loading}
                      >
                        Clear All
                      </button>
                      <span className="selection-count">
                        {transferModal.selectedApps.size} selected
                      </span>
                    </div>
                  </div>

                  <div className="applications-list">
                    {transferModal.applications.map(app => (
                      <div key={app.application_id} className="application-item">
                        <label className="application-checkbox">
                          <input
                            type="checkbox"
                            checked={transferModal.selectedApps.has(app.application_id)}
                            onChange={() => toggleAppSelection(app.application_id)}
                            disabled={transferModal.loading}
                          />
                          <span className="checkmark"></span>
                        </label>
                        
                        <div className="application-details">
                          <strong className="app-id">{app.application_id_code}</strong>
                          <span className="app-name">{app.application_name}</span>
                          <span className="recommendation-badge" style={{ 
                            backgroundColor: getRecommendationColor(app.recommendation),
                            color: 'white'
                          }}>
                            {app.recommendation}
                          </span>
                          <span className="app-completed">Completed: {app.completed_at_formatted}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            
            {!transferModal.loading && transferModal.applications.length > 0 && (
              <div className="modal-footer">
                <button 
                  className="btn-cancel"
                  onClick={closeTransferModal}
                  disabled={transferModal.loading}
                >
                  Cancel
                </button>
                <button 
                  className="btn-transfer-execute"
                  onClick={executeTransfer}
                  disabled={transferModal.loading || transferModal.selectedApps.size === 0}
                >
                  {transferModal.loading ? 'Transferring...' : `Transfer ${transferModal.selectedApps.size} Application(s)`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default FinalApplicationDispositionModel;